<template>
    <div
        class="tournamentCenterEntranceCard"
        v-on:click="emit('goToOverview')"
    >
        <div
            class="tournamentCenterEntranceMain"
        >
            <div
                class="tournamentCenterEntrancePlayers"
            >
                <picture
                    v-if="homePlayerAvatar"
                >
                    <source
                        v-bind:srcset="homePlayerAvatar.webp"
                        type="image/webp"
                    >
                    <img
                        v-bind:src="homePlayerAvatar.origin"
                        alt=""
                        aria-hidden="true"
                        class="playerAvatar"
                    >
                </picture>
                <picture
                    v-if="awayPlayerAvatar"
                >
                    <source
                        v-bind:srcset="awayPlayerAvatar.webp"
                        type="image/webp"
                    >
                    <img
                        v-bind:src="awayPlayerAvatar.origin"
                        alt=""
                        aria-hidden="true"
                        class="playerAvatar"
                    >
                </picture>
            </div>
            <div class="tournamentCenterEntranceHeader">
                <h2 class="tournamentCenterEntranceTitle">
                    {{ tournamentName }}
                </h2>
                <div class="cta">
                    <button
                        type="button"
                        class="ctaButton"
                    >
                        {{ exploreNowButtonText }}
                    </button>
                </div>
            </div>
        </div>
        <div class="tournamentCenterEntranceMatchBar">
            <span v-if="event.isLive" class="liveBadge">{{ liveTranslation }}</span>
            <span v-if="!event.isLive" class="nextBadge">{{ nextTranslation }}</span>
            <picture
                v-if="homeTeamIcon"
                class="matchBar-flag"
            >
                <source
                    v-bind:srcset="homeTeamIcon.webp"
                    type="image/webp"
                >
                <img
                    v-bind:src="homeTeamIcon.origin"
                    alt=""
                    aria-hidden="true"
                >
            </picture>
            <span
                class="matchBar-teamName"
                v-bind:class="{ _favorite: event.isHomeFavorite }"
            >{{ homeTeamName }}</span>
            <span class="matchBar-vs"> &nbsp;{{ vsTranslation }}&nbsp;</span>
            <picture
                v-if="awayTeamIcon"
                class="matchBar-flag"
            >
                <source
                    v-bind:srcset="awayTeamIcon.webp"
                    type="image/webp"
                >
                <img
                    v-bind:src="awayTeamIcon.origin"
                    alt=""
                    aria-hidden="true"
                >
            </picture>
            <span
                class="matchBar-teamName"
                v-bind:class="{ _favorite: event.isAwayFavorite }"
            >{{ awayTeamName }}</span>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed } from 'vue';
    import type { ITournamentCenterEntranceMatch } from '@/interface/IMatch';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import {
        getTournamentPlayerImage,
        getTournamentTeamImage,
    } from '@/libraries/helpers/tournamentCenterHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';

    interface Props {
        event: ITournamentCenterEntranceMatch;
    }

    const props = defineProps<Props>();

    const emit = defineEmits<(e: 'goToOverview') => void>();
    const vsTranslation = computed(() => i18n.t('vs.asia'));
    const tournamentName = computed(() => i18n.t('tournament_center_event_title'));
    const exploreNowButtonText = computed(() => i18n.t('cta_explore_now'));
    const homeTeamName = computed(() => getTranslated(props.event.homeTeam.teamName));
    const awayTeamName = computed(() => getTranslated(props.event.awayTeam.teamName));

    const homeTeamIcon = computed(() => getTournamentTeamImage(props.event.homeTeam));
    const awayTeamIcon = computed(() => getTournamentTeamImage(props.event.awayTeam));

    const homePlayerAvatar = computed(() => getTournamentPlayerImage(props.event.homeTeam?.countryName));
    const awayPlayerAvatar = computed(() => getTournamentPlayerImage(props.event.awayTeam?.countryName));

    const liveTranslation = computed(() => getTranslationBySite('live').toUpperCase());
    const nextTranslation = computed(() => i18n.t('tournament_center_entrance_up_next'));
</script>
